import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { StudentsAPI } from "@/lib/sprint3-api";
import { SectionsAPI } from "@/lib/sprint2-api";
import { NoticesAPI } from "@/lib/sprint7-api";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Pencil, ChevronLeft, User, Phone, Briefcase, ShieldCheck, AlertCircle, Camera, Users2, UserCheck } from "lucide-react";

function field(label, value, ok) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${ok === false ? "text-rose-600" : ok === true ? "text-emerald-600" : ""}`}>{value || <span className="text-muted-foreground/60 italic">—</span>}</span>
    </div>
  );
}

function Section({ icon, title, children, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
        <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">{icon}</div>
        <h3 className="font-semibold text-lg tracking-tight">{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const sQ = useQuery({
    queryKey: ["student", id],
    queryFn: () => StudentsAPI.get(id),
  });
  const sibQ = useQuery({
    queryKey: ["student-siblings", id],
    queryFn: () => StudentsAPI.siblings(id),
    enabled: !!sQ.data,
  });
  // Class Teacher for this student's section (best-effort lookup — degrades
  // silently to "No class teacher assigned" when the field is missing).
  const ctQ = useQuery({
    queryKey: ["student-ct", sQ.data?.class_id, sQ.data?.section_id],
    queryFn: async () => {
      const secs = await SectionsAPI.list({ class_id: sQ.data.class_id });
      return secs.find((x) => x.id === sQ.data.section_id) || null;
    },
    enabled: !!(sQ.data?.class_id && sQ.data?.section_id),
  });

  const s = sQ.data;
  const fullName = s ? `${s.first_name || ""} ${s.last_name || ""}`.trim() : "";
  const initials = (s ? fullName : "?").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const kycComplete = !!(s?.photo_url && s?.aadhaar_no && s?.apaar_id && s?.contact_email && s?.parent_email);

  return (
    <AppShell>
      <div className="p-6 max-w-5xl mx-auto" data-testid="student-profile-page">
        <Button variant="ghost" onClick={() => navigate("/students")} className="mb-4 -ml-2" data-testid="back-to-students">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to Students
        </Button>

        {sQ.isLoading ? (
          <div className="py-20 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : sQ.isError ? (
          <div className="py-20 text-center text-rose-600">Student not found.</div>
        ) : (
          <>
            {/* Banner */}
            <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-secondary border border-border rounded-xl p-6 mb-5 text-center relative">
              <div className={`mx-auto h-24 w-24 rounded-full flex items-center justify-center font-display font-bold text-2xl overflow-hidden ring-4 ${kycComplete ? "ring-emerald-300" : "ring-rose-300"} bg-card`} style={s.photo_url ? { backgroundImage: `url(${s.photo_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}>
                {!s.photo_url && initials}
              </div>
              <h1 className="font-display font-bold text-3xl tracking-tight mt-3">{fullName}</h1>
              <div className="text-xs text-muted-foreground font-mono mt-1">{s.admission_no}</div>
              {!kycComplete && (
                <Badge className="mt-2 bg-rose-100 text-rose-700 hover:bg-rose-100">
                  <AlertCircle className="h-3 w-3 mr-1" /> KYC completion is due
                </Badge>
              )}
              <Button variant="outline" size="sm" className="absolute top-4 right-4" onClick={() => setEditing(true)} data-testid="edit-student">
                <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Section icon={<User className="h-4 w-4" />} title="Personal Details" testid="sec-personal">
                {field("Account Status", <Badge variant="outline" className={s.is_active ? "bg-emerald-50 text-emerald-700" : ""}>{s.is_active ? "Active" : "Inactive"}</Badge>)}
                {field("Email Verification", s.email_verified ? "Verified" : "Not Verified", s.email_verified)}
                {field("Phone Verification", s.phone_verified ? "Verified" : "Not Verified", s.phone_verified)}
                {field("User ID", <span className="font-mono text-[11px]">{s.id}</span>)}
              </Section>

              <Section icon={<Phone className="h-4 w-4" />} title="Contact Details" testid="sec-contact">
                {field("Mobile No", s.contact_phone)}
                {field("Email Address", s.contact_email)}
                {field("Parent Email", s.parent_email)}
                {field("Address", s.address)}
              </Section>

              <Section icon={<Briefcase className="h-4 w-4" />} title="Student Details" testid="sec-student">
                {field("Gender", s.gender ? s.gender.charAt(0).toUpperCase() + s.gender.slice(1) : "—")}
                {field("Date of Birth", s.dob)}
                {field("Blood Group", s.blood_group)}
                {field("Father's Name", s.father_name)}
                {field("Mother's Name", s.mother_name)}
              </Section>

              <Section icon={<ShieldCheck className="h-4 w-4" />} title="KYC Details" testid="sec-kyc">
                {field("Aadhaar No.", s.aadhaar_no)}
                {field("APAAR ID", s.apaar_id || "Fill in APAAR ID", !!s.apaar_id)}
                {field("Verification Status", kycComplete ? "Verified" : "Pending", kycComplete)}
              </Section>

              <div className="md:col-span-2">
                <Section icon={<UserCheck className="h-4 w-4" />} title="Class Teacher" testid="sec-class-teacher">
                  {ctQ.data?.class_teacher ? (
                    <div className="flex items-center gap-3 py-2" data-testid="ct-info">
                      <div className="h-10 w-10 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm">
                        {(ctQ.data.class_teacher.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{ctQ.data.class_teacher.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          Section {ctQ.data.name} · {ctQ.data.class_teacher.email || "—"}
                        </div>
                      </div>
                      {ctQ.data.class_teacher.phone && (
                        <a href={`tel:${ctQ.data.class_teacher.phone}`} className="text-sm font-mono px-3 py-1.5 border border-border rounded-md hover:bg-muted transition" data-testid="ct-call">
                          <Phone className="h-3 w-3 inline mr-1" /> {ctQ.data.class_teacher.phone}
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="py-2 text-sm text-muted-foreground italic">
                      No class teacher assigned yet. An admin can assign one from <Link to="/classes-sections" className="text-primary hover:underline">Classes &amp; Sections</Link>.
                    </div>
                  )}
                </Section>
              </div>

              <div className="md:col-span-2">
                <Section icon={<Users2 className="h-4 w-4" />} title="Siblings (same campus)" testid="sec-siblings">
                  {sibQ.isLoading ? (
                    <div className="py-2 text-sm text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin inline mr-1" /> Loading…</div>
                  ) : (sibQ.data || []).length === 0 ? (
                    <div className="py-2 text-sm text-muted-foreground italic">
                      No siblings found in this campus. {s.parent_email ? "" : "Set Parent Email to enable sibling discovery."}
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {sibQ.data.map((sib) => (
                        <li key={sib.id}>
                          <Link
                            to={`/students/${sib.id}`}
                            className="flex items-center gap-3 px-3 py-2 border border-border rounded-lg hover:bg-muted/40 transition"
                            data-testid={`sibling-${sib.id}`}
                          >
                            <div className="h-9 w-9 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-xs overflow-hidden" style={sib.photo_url ? { backgroundImage: `url(${sib.photo_url})`, backgroundSize: "cover" } : undefined}>
                              {!sib.photo_url && (sib.first_name?.[0] || "?")}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{sib.first_name} {sib.last_name}</div>
                              <div className="text-xs text-muted-foreground font-mono">{sib.admission_no}</div>
                            </div>
                            <ChevronLeft className="h-4 w-4 rotate-180 text-muted-foreground" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-3 text-[11px] text-muted-foreground">
                    Multi-child support: up to 5 children per parent email. Parents sign in via the same email on the mobile app and see all their kids.
                  </div>
                </Section>
              </div>
            </div>

            <EditStudentDialog student={s} open={editing} onOpenChange={setEditing} onSaved={() => { qc.invalidateQueries({ queryKey: ["student", id] }); qc.invalidateQueries({ queryKey: ["student-siblings", id] }); qc.invalidateQueries({ queryKey: ["students"] }); }} />
          </>
        )}
      </div>
    </AppShell>
  );
}

function EditStudentDialog({ student, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const f = { ...student, ...form };
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: typeof e === "string" ? e : e?.target?.value }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await NoticesAPI.uploadAttachment(file);
      setForm((p) => ({ ...p, photo_url: NoticesAPI.attachmentUrl(res.id) }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const patch = {
        first_name: f.first_name, last_name: f.last_name,
        contact_phone: f.contact_phone, contact_email: f.contact_email,
        parent_email: f.parent_email, father_name: f.father_name, mother_name: f.mother_name,
        aadhaar_no: f.aadhaar_no, apaar_id: f.apaar_id,
        blood_group: f.blood_group, address: f.address, photo_url: f.photo_url,
        email_verified: f.email_verified, phone_verified: f.phone_verified,
      };
      await StudentsAPI.update(student.id, patch);
      toast.success("Student profile updated");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit student profile</DialogTitle>
          <DialogDescription>Mandatory: contact email (parent login on mobile app). Aadhaar + APAAR are KYC mandates.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="cursor-pointer relative">
              <div className="h-20 w-20 rounded-full border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/30" style={f.photo_url ? { backgroundImage: `url(${f.photo_url})`, backgroundSize: "cover", borderStyle: "solid" } : undefined}>
                {!f.photo_url && <Camera className="h-6 w-6 text-muted-foreground" />}
                {uploading && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="h-5 w-5 text-white animate-spin" /></div>}
              </div>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            </label>
            <div className="text-xs text-muted-foreground">Click to change profile photo.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First name</Label><Input value={f.first_name || ""} onChange={set("first_name")} /></div>
            <div><Label>Last name</Label><Input value={f.last_name || ""} onChange={set("last_name")} /></div>
            <div><Label>Mobile</Label><Input value={f.contact_phone || ""} onChange={set("contact_phone")} /></div>
            <div><Label>Email *</Label><Input type="email" value={f.contact_email || ""} onChange={set("contact_email")} /></div>
            <div><Label>Parent Email</Label><Input type="email" value={f.parent_email || ""} onChange={set("parent_email")} placeholder="for sibling grouping" /></div>
            <div><Label>Blood Group</Label><Input value={f.blood_group || ""} onChange={set("blood_group")} /></div>
            <div><Label>Father's Name</Label><Input value={f.father_name || ""} onChange={set("father_name")} /></div>
            <div><Label>Mother's Name</Label><Input value={f.mother_name || ""} onChange={set("mother_name")} /></div>
            <div><Label>Aadhaar No.</Label><Input value={f.aadhaar_no || ""} onChange={set("aadhaar_no")} maxLength={14} placeholder="XXXX XXXX XXXX" /></div>
            <div><Label>APAAR ID</Label><Input value={f.apaar_id || ""} onChange={set("apaar_id")} placeholder="12 digit APAAR" /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={f.address || ""} onChange={set("address")} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} data-testid="save-student">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
